/*
THESIS: Give creators one clear path from a long video to a short worth publishing.
OWN-WORLD: The landing's white canvas, Poppins display type, open runway and semantic accents.
STORY: Add a source, see how moments become drafts, review the limits, then start.
FIRST VIEWPORT: A centered search-intent headline and source form lead to real product demonstrations.
FORM: An extension of the established Review Runway, with a dedicated long-video FAQ.
*/
import MuxPlayer from "@mux/mux-player-react/lazy"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowRight, ChevronDown } from "lucide-react"
import { LandingSourceForm } from "@/components/landing-source-form"
import { PublicNavbar } from "@/components/public-navbar"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import { publicPageHead } from "@/lib/seo"

const title = "AI clip generator for long videos | intoshort"
const description =
  "Use an AI clip generator to turn long videos into short videos. Upload a recording or paste a YouTube or Twitch link. Review vertical clips, adjust captions and export."

export const Route = createFileRoute("/tools/long-video-to-short-video")({
  head: () =>
    publicPageHead({ path: "/tools/long-video-to-short-video", title, description }),
  component: LongVideoToShortVideoPage,
})

const stages = [
  {
    title: "Give the generator your source",
    copy: "Upload your recording or paste a public YouTube or Twitch VOD link you have permission to use. Start with the full video, without preparing individual cuts.",
    playbackId: "7uMZh67r1FzdTdXefH00WtV7WUX9gKNlOiCjITxmPdCc",
    demo: "Adding a video source to intoshort",
  },
  {
    title: "Set your clip generator preferences",
    copy: "Describe the moments you want, choose a target length and set your captions and video quality. The generator looks for passages that fit your direction.",
    playbackId: "C42J3zqX00fPK2Imu4W9XTET01joC00iWy96jcm4j02VRso",
    demo: "Choosing clip generation and caption settings",
  },
  {
    title: "Review the generator's first cuts",
    copy: "Watch the suggestions, check the context and refine the cuts, framing and caption style. Download the shorts you approve when their renders are ready.",
    playbackId: "NloREnvZJmyd01IJp01Glnj9KiASihEUIoVq7JAmflsuU",
    demo: "Reviewing generated shorts before export",
  },
]

const capabilities = [
  ["Source videos", "Upload a video file, or use a public YouTube video or Twitch VOD link. Only process footage you have permission to reuse."],
  ["Moment selection", "AI suggests passages based on your instructions and existing source content. Longer passages can become numbered parts."],
  ["Video formats", "Create vertical 9:16 shorts or export in 16:9. Review the automatic framing and adjust it in the editor."],
  ["Captions", "Automatic captions with adjustable presets, size and position. Accuracy depends on the audio and language; review each draft."],
  ["Editorial control", "Trim cuts, remove unwanted sections and choose which suggestions to export. The source determines how many useful clips are available."],
  ["Export quality", "Up to 1080p on Basic; 1440p and 4K are available on Pro and High volume. A larger export does not add detail missing from the source."],
  ["Free trial", "Try one source video with access to up to three shorts, depending on available moments. Free projects expire after three days."],
]

const reasons = [
  {
    title: "A generator that starts with real moments",
    copy: "Get a review queue of selected passages instead of splitting the whole recording at fixed intervals. Check every suggestion against its source and keep the stories that stand on their own.",
    playbackId: "fQlQuc4B6dOX01Un4w00EyTHzahR2Wzsllcx017QMX64O00",
    alt: "Product demonstration of several short video suggestions from one source",
  },
  {
    title: "A generator with caption settings you choose",
    copy: "Prepare captions with the first draft, then choose their preset, size and position. Watch the result for timing and readability before you export.",
    playbackId: "C42J3zqX00fPK2Imu4W9XTET01joC00iWy96jcm4j02VRso",
    alt: "Product demonstration of the intoshort caption configuration dialog",
  },
  {
    title: "A generator that leaves you the final edit",
    copy: "Remove sections that weaken the moment, adjust the framing and export the version you approve. A generated suggestion is a starting point for your editorial decision.",
    playbackId: "xMcC00oSzfI4sKUG4GTuYlnYL9fYbgyAab300Fut4SNy4",
    alt: "Product demonstration of reviewing a short in the intoshort editor",
  },
]

const questions = [
  {
    question: "How do I turn a long video into a short video?",
    answer: "Add your video, choose the moments and clip length you want, then start processing. intoshort suggests short videos with captions. Review the cuts, context and framing before exporting. You can leave after the source finishes uploading and return when your drafts are ready.",
  },
  {
    question: "Can I convert a long video to shorts for free?",
    answer: "The free trial covers one source video and access to up to three shorts, depending on the moments available. The trial has no separate duration cap, but shared file-size and processing limits apply. Free projects expire three days after creation. See Pricing for the current plan details.",
  },
  {
    question: "Which long videos can I use?",
    answer: "Upload a video file or paste a public YouTube video or Twitch VOD link that you have permission to process. Podcasts, interviews, tutorials and streams can all contain useful passages. Private, age-restricted, region-blocked or otherwise inaccessible links may fail to import.",
  },
  {
    question: "Is there a maximum video length or file size?",
    answer: "Uploads have a 20 GiB file-size limit. There is no single maximum-duration promise: your plan's source-minute allowance, the format and shared technical limits also matter. Long or high-resolution recordings take more time to process.",
  },
  {
    question: "Can I edit the short videos and subtitles?",
    answer: "Yes. You can trim cuts, remove sections, adjust framing and change the subtitle preset, size and position before export. Transcript text is available for review, but manual correction of the transcript text is not currently supported.",
  },
  {
    question: "How many shorts will my long video produce?",
    answer: "You choose a target, but the source determines what is available. intoshort may return fewer suggestions or flag weak matches. Poor audio can affect captions, brief visual moments can be missed, and every draft still needs your review. No tool can guarantee that a clip will go viral.",
  },
]

function LongVideoToShortVideoPage() {
  return (
    <div className="landing landing-home forge-landing long-video-page" id="top">
      <a className="skip-link" href="#contenu">Skip to content</a>
      <PublicNavbar />
      <main id="contenu" className="forge-main">
        <section className="forge-hero page-width" aria-labelledby="hero-title">
          <div className="forge-hero-copy">
            <Badge variant="outline" className="forge-badge">Long video to short video</Badge>
            <h1 id="hero-title">
              <span className="forge-title-main">AI clip generator</span>{" "}
              <span className="forge-title-sub">for <span className="forge-heading-accent">long videos.</span></span>
            </h1>
            <p className="forge-hero-lead">
              Give your best moments a life beyond the full recording. intoshort
              finds passages in your long videos and turns them into editable
              vertical shorts with captions. You choose what makes the cut.
            </p>
            <LandingSourceForm />
            <a className="long-video-workflow-link" href="#how-it-works">
              See how a long video becomes shorts
              <ArrowRight aria-hidden="true" />
            </a>
          </div>
        </section>

        <section className="tool-overview page-width" aria-labelledby="overview-title">
          <div className="long-video-intro">
            <h2 id="overview-title">What can an AI clip generator<br /><span className="forge-heading-accent">do with your footage?</span></h2>
            <p>intoshort turns selected passages from a long recording into short video drafts with captions and framing. Start with a podcast, interview, tutorial or stream, then review the moments before you publish them.</p>
          </div>
          <Table>
            <colgroup><col className="tool-label-column" /><col /></colgroup>
            <caption className="sr-only">IntoShort AI clip generator capabilities and limits</caption>
            <TableBody>
              {capabilities.map(([label, value]) => (
                <TableRow key={label}>
                  <th scope="row">{label}</th>
                  <TableCell>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="long-video-plan-link"><Link to="/pricing">See generator plans and trial limits <ArrowRight aria-hidden="true" /></Link></p>
        </section>

        <section className="tool-before-after page-width" aria-labelledby="before-after-title">
          <div className="long-video-intro">
            <h2 id="before-after-title">Before and after:<br /><span className="forge-heading-accent">the clip generator in action.</span></h2>
            <p>See the same footage move from a widescreen source to a captioned vertical composition in this demonstration from our landing page. Check the framing on your own clips before export.</p>
          </div>
          <MuxPlayer
            className="forge-demo-video rounded-md"
            style={{ aspectRatio: "226 / 135" }}
            loading="viewport"
            preload="none"
            playbackId="dGiKBjkGFFcsIX86RCsKguYKHh02XvQaEcPTjM6Hk02oc"
            aria-label="Before and after demonstration of widescreen to vertical reframing"
            autoPlay="muted" muted loop playsInline nohotkeys
          />
        </section>

        <section id="how-it-works" className="forge-how forge-workflow page-width" aria-labelledby="workflow-title">
          <div className="long-video-intro">
            <h2 id="workflow-title">Use the AI clip generator<br /><span className="forge-heading-accent">in three steps.</span></h2>
            <p>Bring the recording. Set the direction. Review what is worth sharing.</p>
          </div>
          <ol className="forge-workflow-steps">
            {stages.map((stage) => (
              <li key={stage.playbackId}>
                <MuxPlayer
                  className="forge-demo-video rounded-md"
                  style={{ aspectRatio: "226 / 135" }}
                  loading="viewport" preload="none"
                  playbackId={stage.playbackId} aria-label={stage.demo}
                  autoPlay="muted" muted loop playsInline nohotkeys
                />
                <h3>{stage.title}</h3>
                <p>{stage.copy}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="tool-reasons page-width" aria-labelledby="reasons-title">
          <div className="long-video-intro">
            <h2 id="reasons-title">Why choose intoshort<br /><span className="forge-heading-accent">as your AI clip generator?</span></h2>
            <p>Spend your review on the moment, the context and the edit. Here is what that looks like inside the product.</p>
          </div>
          <div className="tool-reason-list">
            {reasons.map((reason) => (
              <article key={reason.playbackId}>
                <img
                  src={`https://image.mux.com/${reason.playbackId}/thumbnail.webp?width=1000&time=1`}
                  alt={reason.alt} width={1000} height={597} loading="lazy" decoding="async"
                  className="rounded-lg"
                />
                <div><h3>{reason.title}</h3><p>{reason.copy}</p></div>
              </article>
            ))}
          </div>
        </section>

        <section id="questions" className="forge-faq page-width" aria-labelledby="faq-title">
          <div className="forge-faq-heading">
            <h2 id="faq-title" className="mx-auto text-center">AI clip generator.<br /><span className="forge-heading-accent">Your questions, answered.</span></h2>
          </div>
          <div className="forge-faq-list mx-auto">
            {questions.map((item) => (
              <details key={item.question}>
                <summary>{item.question}<ChevronDown aria-hidden="true" /></summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
          <p className="long-video-plan-link"><Link to="/pricing">Compare plans and trial limits <ArrowRight aria-hidden="true" /></Link></p>
        </section>

        <section className="forge-final-cta page-width" aria-labelledby="cta-title">
          <div>
            <p>Your next short is in the footage.</p>
            <h2 id="cta-title">Put the clip generator<br /><span className="forge-heading-accent">to work.</span></h2>
            <span>Start with one video. Review the suggestions. Make them yours.</span>
          </div>
          <Link to="/app" className={buttonVariants({ size: "lg", className: "forge-primary-link" })}>
            Create your shorts<ArrowRight data-icon="inline-end" aria-hidden="true" />
          </Link>
        </section>
        <section className="long-video-sources page-width" aria-labelledby="sources-title">
          <h2 id="sources-title">Find the right generator<br /><span className="forge-heading-accent">for your source.</span></h2>
          <div>
            <Link to="/tools/youtube-to-shorts"><span>YouTube Shorts generator</span><ArrowRight aria-hidden="true" /></Link>
            <Link to="/tools/twitch-vod-to-shorts"><span>Twitch clip generator</span><ArrowRight aria-hidden="true" /></Link>
            <Link to="/tools/gameplay-facecam-to-shorts"><span>Gameplay &amp; facecam clip generator</span><ArrowRight aria-hidden="true" /></Link>
          </div>
        </section>

      </main>
    </div>
  )
}
